<template>
  <Tooltip v-if="contributors.length" placement="bottom-start">
    <!-- Focusable, so the names in the tooltip reach a keyboard too. -->
    <ul
      class="flex items-center rounded-full focus-visible:ring-2 focus-visible:ring-purple-900 focus-visible:outline-none"
      tabindex="0"
      data-testid="dashboard-contributors"
      :aria-label="summary"
    >
      <li
        v-for="(contributor, index) in visible"
        :key="identity(contributor)"
        :class="index > 0 ? '-ml-2' : ''"
      >
        <Avatar
          :user="asAvatarUser(contributor)"
          :size="size"
          class="ring-2 ring-white"
        />
      </li>
      <li
        v-if="overflow > 0"
        class="-ml-2 flex items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-700 ring-2 ring-white"
        :class="size"
        data-testid="dashboard-contributors-overflow"
      >
        {{ overflowLabel }}
      </li>
    </ul>

    <template #popper>
      <div class="min-w-56 p-1">
        <p class="mb-2 text-xs font-semibold text-gray-500 uppercase">
          Contributors, last {{ CONTRIBUTORS_WINDOW_DAYS }} days
        </p>
        <ul class="flex flex-col gap-2">
          <li
            v-for="contributor in listed"
            :key="identity(contributor)"
            class="flex items-center gap-2"
            data-testid="dashboard-contributor"
          >
            <Avatar :user="asAvatarUser(contributor)" size="size-6" />
            <span class="grow text-sm text-gray-800">
              {{ label(contributor) }}
            </span>
            <span class="shrink-0 text-xs text-gray-500">
              {{ when(contributor) }}
            </span>
          </li>
        </ul>
      </div>
    </template>
  </Tooltip>
</template>

<script setup lang="ts">
import { PropType, computed } from "vue";
import { Tooltip } from "floating-vue";
import { DateTime } from "luxon";
import Avatar from "@/components/Core/Avatar.vue";
import {
  CONTRIBUTORS_WINDOW_DAYS,
  type DashboardContributor,
} from "@/services/dashboardRevisions";
import { useUsersQuery } from "@/services/user";
import {
  authorAvatarUser,
  authorName,
  contributorCount,
  contributorsCut,
  listedContributors,
} from "@/views/Dashboard/revisionAuthor";

const props = defineProps({
  contributors: {
    type: Array as PropType<DashboardContributor[]>,
    default: () => [],
  },
  // How many avatars the collapsed stack shows before it counts the rest.
  stack: { type: Number, default: 3 },
  size: { type: String, default: "size-6" },
});

const users = useUsersQuery();

const listed = computed(() => listedContributors(props.contributors));
const visible = computed(() => listed.value.slice(0, props.stack));
const overflow = computed(() => Math.max(0, listed.value.length - props.stack));
// "5+" when the server had more than it lists.
const overflowLabel = computed(() =>
  contributorsCut(props.contributors)
    ? `${overflow.value}+`
    : `+${overflow.value}`,
);

// A person and the AI acting for them are two contributors, so the actor
// alone does not identify one.
function identity(contributor: DashboardContributor): string {
  return `${contributor.actorKind}:${contributor.actor ?? "none"}`;
}

function asAvatarUser(contributor: DashboardContributor) {
  return authorAvatarUser(contributor, users.data);
}

function label(contributor: DashboardContributor): string {
  return authorName(contributor) ?? "Unknown";
}

function when(contributor: DashboardContributor): string {
  return DateTime.fromISO(contributor.lastEditedAt).toRelative() ?? "";
}

// Includes the names, not just the count, for screen readers.
const summary = computed(
  () =>
    `${contributorCount(props.contributors)} in the last ${CONTRIBUTORS_WINDOW_DAYS} days: ${listed.value
      .map(label)
      .join(", ")}`,
);
</script>
